import type { AnalyzedMoveResult } from "@/shared/api/engine.api";
import { teachingMarkerHighlightSquares } from "@/features/board/review/teaching-explainer";
import { boardPositionMarkers } from "@/features/board/review/position-markers";
import type { ReviewBoardSettings } from "@/features/board/review-board-settings";
import {
  chessArrow,
  PLAYED_MOVE_ARROW_COLOR,
  PLAYED_MOVE_SQUARE_TINT,
  type ChessArrow,
  type ChessSquare,
  type ChessSquareStyles,
} from "@/shared/components/chessboard";

export const inlineRelationshipPresentation = (
  fen: string,
  settings: ReviewBoardSettings,
  focusSquare?: string | null,
): { arrows: ChessArrow[]; squareStyles: ChessSquareStyles } => {
  const markers = boardPositionMarkers(fen, settings, focusSquare);
  return {
    arrows: markers.map((marker) => chessArrow(marker.from as ChessSquare, marker.to as ChessSquare, marker.color)),
    squareStyles: Object.fromEntries(
      markers.flatMap((marker) =>
        teachingMarkerHighlightSquares(marker).map((square) => [square, { backgroundColor: marker.tint }]),
      ),
    ),
  };
};

type InlineMoveEvidence = Pick<
  AnalyzedMoveResult,
  "playedFrom" | "playedTo" | "bestMoveFrom" | "bestMoveTo" | "isPlayerMove" | "centipawnLoss"
>;

export const inlineMoveEvidencePresentation = (
  move: InlineMoveEvidence,
): { arrows: ChessArrow[]; squareStyles: ChessSquareStyles } => {
  const playedFrom = move.playedFrom as ChessSquare;
  const playedTo = move.playedTo as ChessSquare;
  const squareStyles = {
    [playedFrom]: { backgroundColor: PLAYED_MOVE_SQUARE_TINT },
    [playedTo]: { backgroundColor: PLAYED_MOVE_SQUARE_TINT },
  };
  const meaningfulAlternative =
    move.bestMoveFrom &&
    move.bestMoveTo &&
    move.isPlayerMove &&
    (move.bestMoveFrom !== move.playedFrom || move.bestMoveTo !== move.playedTo) &&
    move.centipawnLoss >= 50;

  return {
    arrows: meaningfulAlternative
      ? [chessArrow(move.bestMoveFrom as ChessSquare, move.bestMoveTo as ChessSquare, "rgb(34,197,94)")]
      : [chessArrow(playedFrom, playedTo, PLAYED_MOVE_ARROW_COLOR)],
    squareStyles,
  };
};
